import React, { useState, useMemo } from 'react';
import { useI18n } from '../i18n';
import { DollarSign, AlertTriangle, CheckCircle2, RotateCcw, Zap, ShieldCheck } from 'lucide-react';
import { trackBudgetSimulate, trackBudgetCurrencyChange } from '../lib/analytics';

type Currency = 'USD' | 'EUR' | 'TRY' | 'GBP';

interface CurrencyConfig {
  code: Currency;
  symbol: string;
  rate: number; // Conversion from 1 USD
}

const currencies: Record<Currency, CurrencyConfig> = {
  USD: { code: 'USD', symbol: '$', rate: 1.0 },
  EUR: { code: 'EUR', symbol: '€', rate: 0.92 },
  TRY: { code: 'TRY', symbol: '₺', rate: 36.5 },
  GBP: { code: 'GBP', symbol: '£', rate: 0.79 },
};

export const BudgetSimulator: React.FC = () => {
  const { t } = useI18n();
  const [budgetCapUsd, setBudgetCapUsd] = useState<number>(10.0);
  const [spentUsd, setSpentUsd] = useState<number>(9.95);
  const [currency, setCurrency] = useState<Currency>('USD');
  const [lastAction, setLastAction] = useState<string | null>(null);

  const activeCurrency = currencies[currency];

  const remainingUsd = useMemo(() => {
    return Number((budgetCapUsd - spentUsd).toFixed(2));
  }, [budgetCapUsd, spentUsd]);

  const isOverdraft = remainingUsd < 0;

  const formatCurrency = (usdAmount: number): string => {
    const converted = usdAmount * activeCurrency.rate;
    return `${activeCurrency.symbol}${converted.toFixed(2)}`;
  };

  const handleSimulateNormal = () => {
    if (isOverdraft) return;
    const nextSpent = Number((spentUsd + 0.05).toFixed(2));
    setSpentUsd(nextSpent);
    setLastAction('Simulated standard GPT-4o turn ($0.05 USD)');
    trackBudgetSimulate('simulate_normal', {
      cost_usd: 0.05,
      spent_usd: nextSpent,
      remaining_usd: Number((budgetCapUsd - nextSpent).toFixed(2)),
    });
  };

  const handleTriggerOverdraft = () => {
    if (isOverdraft) return;
    const nextSpent = Number((spentUsd + 0.2).toFixed(2));
    setSpentUsd(nextSpent);
    setLastAction('Executed multi-turn reasoning prompt ($0.20 USD) causing soft-overdraft');
    trackBudgetSimulate('trigger_overdraft', {
      cost_usd: 0.2,
      spent_usd: nextSpent,
      remaining_usd: Number((budgetCapUsd - nextSpent).toFixed(2)),
    });
  };

  const handleReset = () => {
    setSpentUsd(9.95);
    setBudgetCapUsd(10.0);
    setLastAction(null);
    trackBudgetSimulate('reset_simulation', {
      budget_cap_usd: 10.0,
      spent_usd: 9.95,
    });
  };

  const handleCurrencyChange = (newCurrency: Currency) => {
    setCurrency(newCurrency);
    trackBudgetCurrencyChange(newCurrency);
  };

  return (
    <section id="budget" className="py-20 md:py-28 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold-500/10 border border-gold-500/30 text-xs font-semibold text-gold-600 dark:text-gold-400 mb-4 uppercase tracking-wider">
            <DollarSign className="w-3.5 h-3.5" />
            <span>{t.budgetSimulator.badge}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight mb-4">
            {t.budgetSimulator.title}
          </h2>
          <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300">
            {t.budgetSimulator.subtitle}
          </p>
        </div>

        {/* Interactive Simulator Container */}
        <div className="max-w-4xl mx-auto glow-card rounded-3xl p-6 sm:p-10 border-slate-200 dark:border-navy-700/80">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-6 border-b border-slate-200 dark:border-navy-800/80">
            <div>
              <span className="text-xs uppercase font-mono tracking-widest text-slate-500 dark:text-slate-400">
                Team Workspace: Engineering Alpha
              </span>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                Active Quota Guardrails
              </h3>
            </div>

            {/* Currency Selector */}
            <div className="flex items-center gap-1.5 p-1 rounded-xl bg-slate-100 dark:bg-navy-950/80 border border-slate-300 dark:border-navy-800">
              {(Object.keys(currencies) as Currency[]).map((c) => (
                <button
                  key={c}
                  onClick={() => handleCurrencyChange(c)}
                  className={`px-2.5 py-1 text-xs font-bold rounded-lg transition-colors ${
                    currency === c
                      ? 'bg-gold-500 text-navy-950 shadow-sm'
                      : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {/* Budget Controls & KPI Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 my-8">
            {/* Slider: Cap */}
            <div className="p-5 rounded-2xl bg-slate-50 dark:bg-navy-950/60 border border-slate-200 dark:border-navy-800/60 flex flex-col justify-between">
              <div>
                <span className="text-xs font-medium text-slate-500 dark:text-slate-400">{t.budgetSimulator.teamBudgetLabel}</span>
                <div className="text-2xl font-extrabold text-slate-900 dark:text-white mt-1 font-mono">
                  {formatCurrency(budgetCapUsd)}
                </div>
              </div>
              <div className="mt-4">
                <input
                  type="range"
                  min="5.00"
                  max="100.00"
                  step="5.00"
                  value={budgetCapUsd}
                  onChange={(e) => setBudgetCapUsd(parseFloat(e.target.value))}
                  className="w-full accent-gold-500 cursor-pointer"
                />
                <div className="flex justify-between text-[10px] text-slate-400 font-mono mt-1">
                  <span>Min: $5.00</span>
                  <span>$100.00</span>
                </div>
              </div>
            </div>

            {/* Spent */}
            <div className="p-5 rounded-2xl bg-slate-50 dark:bg-navy-950/60 border border-slate-200 dark:border-navy-800/60 flex flex-col justify-between">
              <div>
                <span className="text-xs font-medium text-slate-500 dark:text-slate-400">{t.budgetSimulator.spentLabel}</span>
                <div className="text-2xl font-extrabold text-slate-700 dark:text-slate-300 mt-1 font-mono">
                  {formatCurrency(spentUsd)}
                </div>
              </div>
              <div className="mt-4 flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                <ShieldCheck className="w-4 h-4 text-gold-500" />
                <span>Canonical USD Tracking</span>
              </div>
            </div>

            {/* Remaining Balance (with Overdraft styling) */}
            <div
              className={`p-5 rounded-2xl border flex flex-col justify-between transition-all ${
                isOverdraft
                  ? 'bg-red-50 dark:bg-red-950/30 border-red-300 dark:border-red-500/60 shadow-lg shadow-red-500/10'
                  : 'bg-gold-500/10 border-gold-500/40 shadow-gold-glow'
              }`}
            >
              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                  {t.budgetSimulator.currentBalanceLabel}
                </span>
                <div
                  className={`text-2xl sm:text-3xl font-extrabold mt-1 font-mono ${
                    isOverdraft ? 'text-red-600 dark:text-red-400' : 'text-gold-600 dark:text-gold-400'
                  }`}
                >
                  {formatCurrency(remainingUsd)}
                </div>
              </div>

              <div className="mt-4">
                <span
                  className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                    isOverdraft
                      ? 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400 border border-red-300 dark:border-red-500/40'
                      : 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-500/40'
                  }`}
                >
                  {isOverdraft ? t.budgetSimulator.statusOverdraft : t.budgetSimulator.statusActive}
                </span>
              </div>
            </div>
          </div>

          {/* Overdraft Alert Banner */}
          {isOverdraft && (
            <div className="mb-6 p-4 rounded-xl bg-red-50 dark:bg-red-950/50 border border-red-300 dark:border-red-500/60 flex items-start gap-3 animate-fade-in text-red-800 dark:text-red-200">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
              <div>
                <h4 className="text-xs sm:text-sm font-bold text-red-900 dark:text-red-300">
                  {t.budgetSimulator.overdraftBannerTitle}
                </h4>
                <p className="text-xs text-red-700 dark:text-red-200/80 mt-0.5">
                  {t.budgetSimulator.overdraftBannerDesc}
                </p>
              </div>
            </div>
          )}

          {/* Action Simulation Buttons */}
          <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-slate-200 dark:border-navy-800/80">
            <button
              onClick={handleSimulateNormal}
              disabled={isOverdraft}
              className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-navy-850 dark:hover:bg-navy-800 text-xs sm:text-sm font-bold text-slate-900 dark:text-slate-100 border border-slate-300 dark:border-navy-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-2"
            >
              <Zap className="w-4 h-4 text-gold-500" />
              <span>{t.budgetSimulator.simulatePromptButton}</span>
            </button>

            <button
              onClick={handleTriggerOverdraft}
              disabled={isOverdraft}
              className="px-4 py-2.5 rounded-xl bg-gold-500 hover:bg-gold-400 active:bg-gold-600 text-navy-950 text-xs sm:text-sm font-bold shadow-gold-glow disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-2"
            >
              <AlertTriangle className="w-4 h-4 text-navy-950" />
              <span>{t.budgetSimulator.simulateOverdraftButton}</span>
            </button>

            <button
              onClick={handleReset}
              className="ml-auto px-3.5 py-2.5 rounded-xl bg-transparent hover:bg-slate-100 dark:hover:bg-navy-850 text-xs text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors flex items-center gap-1.5"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>{t.budgetSimulator.resetButton}</span>
            </button>
          </div>

          {lastAction && (
            <div className="mt-4 text-[11px] font-mono text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-gold-500" />
              <span>{lastAction}</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
